import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  addCategorySearchGames,
  addGame,
  addGamesCategory,
  addGridGameSession,
  removeSession,
  setDesktop,
  setGameScreenLayout,
  setSelectedIndex,
  toggleGameType,
  toggleShowGamesTree,
} from '@libs/store/game/actions';
import {
  addGamesToCatProv,
  gamesEndlessLoading,
  setFiltersCategory,
  setFiltersProvider,
  setFiltersWord,
  setGames,
  setLoading,
  setSearcherShow,
  toggleCat,
  toggleProvider,
} from '@libs/store/searcher/actions';
import { selectBookmarks } from '@libs/store/auth/selectors';
import {
  selectEndlessGames,
  selectFilters,
  selectFiltersWord,
  selectSearcherList,
  selectSearcherLoading,
  selectSearcherShow,
  selectTree,
  selectSearcherGamesCategories,
} from '@libs/store/searcher/selectors';
import { GAME_SCREEN_LAYOUT, IGrigGameSession } from '@libs/store/game/model';
import {
  selectGameScreenLayout,
  selectGridGameSessions,
  selectDesktop,
  selectGameType,
  selectGAmeActivity,
  selectSelectedIndex,
  selectGamesCategories,
  selectEnabledGamesProviders,
  selectShowGamesTree,
  selectGameOffice,
  selectAllGamesProviders,
} from '@libs/store/game/selectors';
import { IGame, TAB_SECTION } from '@libs/interfaces';
import { selectlobbyTab } from '@libs/store/settings/selectors';
import { LocalstorageService } from '../landing/localstorage.service';

@Injectable({
  providedIn: 'root',
})
export class CasinoStoreService {
  constructor(
    private store: Store,
    private localstorageService: LocalstorageService,
  ) {}

  /**
   * Selects the game screen layout from the store.
   * @returns {Observable<GAME_SCREEN_LAYOUT>} An observable of the game screen layout.
   */
  selectGameScreenLayout() {
    return this.store.select(selectGameScreenLayout);
  }

  /**
   * Selects the grid game sessions from the store.
   * @returns {Observable<IGrigGameSession[]>} An observable of the grid game sessions.
   */
  selectGridGameSessions() {
    return this.store.select(selectGridGameSessions);
  }

  /**
   * Selects the desktop state from the store.
   * @returns {Observable<boolean>} An observable of the desktop state.
   */
  selectDesktop() {
    return this.store.select(selectDesktop);
  }

  /**
   * Selects the game type from the store.
   * @returns {Observable<string>} An observable of the game type.
   */
  selectGameType() {
    return this.store.select(selectGameType);
  }

  /**
   * Selects the endless games from the store.
   * @returns {Observable<IGame[]>} An observable of the endless games.
   */
  selectEndlessGames() {
    return this.store.select(selectEndlessGames);
  }

  /**
   * Selects the game activity from the store.
   * @returns {Observable<any>} An observable of the game activity.
   */
  selectGameActivity() {
    return this.store.select(selectGAmeActivity);
  }

  /**
   * Selects the selected index from the store.
   * @returns {Observable<number>} An observable of the selected index.
   */
  selectSelectedIndex() {
    return this.store.select(selectSelectedIndex);
  }

  /**
   * Selects the games providers from the store.
   * @returns {Observable<string[]>} An observable of the games providers.
   */
  selectGamesProviders() {
    return this.store.select(selectEnabledGamesProviders);
  }

  /**
   * Selects all games providers from the store.
   * @returns {Observable<IGameProvider[]>} An observable of all games providers.
   */
  selectAllGamesProviders() {
    return this.store.select(selectAllGamesProviders);
  }
  /**
   * Selects the searcher loading state from the store.
   * @returns {Observable<boolean>} An observable of the searcher loading state.
   */
  selectSearcherLoading() {
    return this.store.select(selectSearcherLoading);
  }

  /**
   * Selects the tree from the store.
   * @returns {Observable<any>} An observable of the tree.
   */
  selectTree() {
    return this.store.select(selectTree);
  }

  /**
   * Selects the searcher show state from the store.
   * @returns {Observable<boolean>} An observable of the searcher show state.
   */
  selectSearcherShow() {
    return this.store.select(selectSearcherShow);
  }

  /**
   * Selects the searcher list from the store.
   * @returns {Observable<IGame[]>} An observable of the searcher list.
   */
  selectSearcherList() {
    return this.store.select(selectSearcherList);
  }

  /**
   * Selects the games categories from the store.
   * @returns {Observable<string[]>} An observable of the games categories.
   */
  selectGamesCategories() {
    return this.store.select(selectGamesCategories);
  }

  /**
   * Selects the bookmarks from the store.
   * @returns {Observable<IGame[]>} An observable of the bookmarks.
   */
  selectBookmarks() {
    return this.store.select(selectBookmarks);
  }

  /**
   * Selects the searcher games categories from the store.
   * @returns {Observable<string[]>} An observable of the searcher games categories.
   */
  selectSearcherGamesCategories() {
    return this.store.select(selectSearcherGamesCategories);
  }

  /**
   * Selects the show games tree state from the store.
   * @returns {Observable<boolean>} An observable of the show games tree state.
   */
  selectShowGamesTree() {
    return this.store.select(selectShowGamesTree);
  }

  /**
   * Selects the lobby tab from the store.
   * @returns {Observable<string>} An observable of the lobby tab.
   */
  selectlobbyTab() {
    return this.store.select(selectlobbyTab);
  }

  /**
   * Selects the game activity from the store.
   * @returns {Observable<any>} An observable of the game activity.
   */
  selectGAmeActivity() {
    return this.store.select(selectGAmeActivity);
  }
  selectFiltersWord() {
    return this.store.select(selectFiltersWord);
  }
  /**
   * Sets the filters provider in the store.
   * @param {string} provider - The provider to set.
   */
  setFiltersProvider(provider) {
    this.store.dispatch(setFiltersProvider({ provider }));
  }

  /**
   * Sets the filters word in the store.
   * @param {string} word - The word to set.
   */
  setFiltersWord(word: string) {
    this.store.dispatch(setFiltersWord({ word }));
  }

  /**
   * Closes the searcher by setting the show state to false.
   */
  closeSearcher() {
    this.store.dispatch(setSearcherShow({ show: false }));
  }

  /**
   * Opens the searcher by setting the show state to true.
   */
  openSearcher() {
    this.store.dispatch(setSearcherShow({ show: true }));
  }

  /**
   * Sets the searcher show state in the store.
   * @param {boolean} show - The show state to set.
   */
  setSearcherShow(show: boolean) {
    this.store.dispatch(setSearcherShow({ show }));
  }

  /**
   * Sets the selected index in the store.
   * @param {number} selectedIndex - The index to set.
   */
  setSelectedIndex(selectedIndex: number) {
    this.store.dispatch(setSelectedIndex({ selectedIndex }));
  }

  /**
   * Toggles a category in the store.
   * @param {string} cat - The category to toggle.
   */
  toggleCat(cat) {
    this.store.dispatch(toggleCat({ cat }));
  }

  /**
   * Adds games to a category in the store.
   * @param {IGame[]} games - The games to add.
   * @param {TAB_SECTION} section - The section to add the games to.
   */
  addGamesCategory(games: IGame[], section: TAB_SECTION) {
    this.store.dispatch(addGamesCategory({ games, section }));
  }

  /**
   * Toggles the show games tree state in the store.
   */
  toggleShowGamesTree() {
    this.store.dispatch(toggleShowGamesTree());
  }
  /**
   * Loads the first activity in the store.
   */
  loadFirstActivity() {
    this.store.dispatch({ type: '[Game Session] on start app load activities' });
  }

  /**
   * Toggles a provider in the tree.
   * @param {string} provider - The provider to toggle.
   * @param {string} cat - The category of the provider.
   */
  treeToggleProvider(provider, cat) {
    this.store.dispatch(toggleProvider({ provider, cat }));
  }

  /**
   * Adds category search games to the store.
   * @param {IGame[]} categorySearchGames - The games to add.
   */
  addCategorySearchGames(categorySearchGames: IGame[]) {
    this.store.dispatch(addCategorySearchGames({ categorySearchGames }));
  }

  /**
   * Sets the games in the store.
   * @param {IGame[]} games - The games to set.
   */
  setGames(games: IGame[]) {
    this.store.dispatch(setGames({ games }));
  }

  /**
   * Sets the loading state in the store.
   * @param {boolean} loading - The loading state to set.
   */
  setLoading(loading: boolean) {
    this.store.dispatch(setLoading({ loading }));
  }

  /**
   * Adds a grid game session to the store.
   * @param {number} index - The index of the session.
   * @param {IGrigGameSession} gridGameSession - The session to add.
   */
  addGridGameSession(index: number, gridGameSession: IGrigGameSession) {
    this.store.dispatch(addGridGameSession({ index, gridGameSession }));
  }

  /**
   * Adds games to a category and provider in the store.
   * @param {string} cat - The category.
   * @param {string} prov - The provider.
   * @param {IGame[]} games - The games to add.
   */
  addGamesToCatProv(cat: string, prov: string, games: IGame[]) {
    this.store.dispatch(addGamesToCatProv({ cat, prov, games }));
  }

  /**
   * Triggers the games endless loading in the store.
   */
  gamesEndlessLoading() {
    this.store.dispatch(gamesEndlessLoading());
  }

  /**
   * Removes a session from the store.
   * @param {number} index - The index of the session to remove.
   */
  removeSession(index: number) {
    this.store.dispatch(removeSession({ index }));
  }

  /**
   * Toggles the game type in the store.
   */
  toggleGameType() {
    this.store.dispatch(toggleGameType());
  }

  /**
   * Sets the game screen layout in the store and local storage.
   * @param {GAME_SCREEN_LAYOUT} game_screen_layout - The layout to set.
   */
  setGameScreenLayout(game_screen_layout: GAME_SCREEN_LAYOUT) {
    this.store.dispatch(setGameScreenLayout({ game_screen_layout }));
    this.localstorageService.storeGameScreenLayout(game_screen_layout);
  }

  /**
   * Sets the desktop state in the store.
   * @param {boolean} desktop - The desktop state to set.
   */
  setDesktop(desktop) {
    this.store.dispatch(setDesktop({ desktop }));
  }

  /**
   * Selects the filters from the store.
   * @returns {Observable<any>} An observable of the filters.
   */
  selectFilters() {
    return this.store.select(selectFilters);
  }

  /**
   * Sets the filters category in the store.
   * @param {string} category - The category to set.
   */
  setFiltersCategory(category) {
    this.store.dispatch(setFiltersCategory({ category }));
  }
  addGame(game: IGame) {
    this.store.dispatch(addGame({ game }));
  }
  selectGameOffice() {
    return this.store.select(selectGameOffice);
  }
  loadGamesCategories() {
    this.store.dispatch({ type: '[Searcher Session] Load games categories' });
  }
}
